import { Link } from "react-router-dom";
import { ArrowRight, BarChart3, Images, Link2, Share2, Store, Megaphone, MessageCircle, Smartphone } from "lucide-react";
import { Logo } from "../components/Logo";
import { Button } from "../components/ui/button";
import { useAuth } from "../context/AuthContext";
import { useSettings } from "../context/SettingsContext";
import { InstallButton } from "../components/InstallButton";

const STEPS = [
  { title: "Daftar & pilih link toko", text: "Buat akun dengan email atau Google, tentukan nama dan link toko publikmu." },
  { title: "Tambah produk affiliate", text: "Upload foto, isi harga, tempel link Shopee/TikTok — atau jual lewat WhatsApp." },
  { title: "Bagikan & pantau klik", text: "Taruh link toko di bio sosial media, lalu lihat statistik klik harian." },
];

const FEATURES = [
  { icon: Store, title: "Toko publik sendiri", text: "Link unik jufi/nama-toko lengkap dengan profil, jam buka dan tombol WhatsApp.", span: "lg:col-span-2" },
  { icon: Images, title: "Multi foto produk", text: "Foto produk bergeser otomatis di katalog." },
  { icon: BarChart3, title: "Statistik klik", text: "Pantau klik Shopee & TikTok per hari dan per produk." },
  { icon: Megaphone, title: "Breaking news", text: "Teks berjalan untuk promo dan pengumuman toko." },
  { icon: Share2, title: "Share produk", text: "Bagikan link produk ke WhatsApp atau salin link sekali klik." },
  { icon: MessageCircle, title: "Jual via WhatsApp", text: "Tanpa link affiliate? Pembeli langsung chat ke nomor tokomu.", span: "lg:col-span-2" },
  { icon: Smartphone, title: "Bisa di-install", text: "Pasang di layar HP seperti aplikasi, ringan dan cepat." },
];

const PreviewCard = ({ img, name, price, old }) => (
  <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
    <img src={img} alt={name} className="aspect-square w-full object-cover" />
    <div className="p-1.5 sm:p-2.5">
      <p className="truncate text-[10px] sm:text-xs font-semibold">{name}</p>
      <p className="text-[10px] sm:text-xs font-bold">{price} <span className="hidden sm:inline font-normal text-slate-400 line-through">{old}</span></p>
      <div className="mt-2 grid grid-cols-2 gap-1">
        <span className="rounded-md bg-[#EE4D2D] py-1 text-center text-[8px] sm:text-[10px] font-semibold text-white">Shopee</span>
        <span className="rounded-md bg-slate-900 py-1 text-center text-[8px] sm:text-[10px] font-semibold text-white">TikTok</span>
      </div>
    </div>
  </div>
);

const StorePreview = () => (
  <div className="relative rounded-[28px] border border-slate-200 bg-white p-2 sm:p-3 shadow-[0_30px_80px_rgba(15,23,42,0.12)] sm:rotate-1 hover:rotate-0 transition-transform duration-500">
    <div className="overflow-hidden rounded-2xl bg-[#F8FAFC] min-w-0">
      <div className="bg-slate-900 py-1.5 overflow-hidden whitespace-nowrap text-[10px] font-medium text-white">
        <span className="inline-block marquee-track">Promo 10.10 diskon s/d 70% · Gratis ongkir semua produk · Promo 10.10 diskon s/d 70% · Gratis ongkir semua produk ·</span>
      </div>
      <img src="/img/header.jpg" alt="" className="h-24 w-full object-cover" />
      <div className="px-4 -mt-7">
        <div className="relative z-10 h-14 w-14 rounded-full border-4 border-white bg-slate-900 grid place-items-center text-white font-bold">R</div>
        <p className="mt-2 font-bold text-sm">Rina Picks</p>
        <p className="text-[11px] text-slate-500">Fashion · Buka 08.00–21.00</p>
      </div>
      <div className="grid grid-cols-3 gap-1.5 sm:gap-2 p-2.5 sm:p-4">
        <PreviewCard img="/img/sneakers.jpg" name="Sneakers Putih" price="Rp189rb" old="Rp299rb" />
        <PreviewCard img="/img/headphones.jpg" name="Headphone Wireless" price="Rp249rb" old="Rp399rb" />
        <PreviewCard img="/img/skincare.jpg" name="Serum Glow" price="Rp79rb" old="Rp120rb" />
      </div>
    </div>
  </div>
);

export default function Landing() {
  const { user } = useAuth();
  const { settings } = useSettings();
  return (
    <div className="min-h-screen bg-white">
      <header className="sticky top-0 z-40 backdrop-blur-md bg-white/80 border-b border-slate-200/80">
        <div className="mx-auto max-w-6xl flex items-center justify-between px-4 sm:px-6 h-16">
          <Logo />
          <div className="flex items-center gap-1.5 sm:gap-2">
            <InstallButton className="hidden sm:inline-flex" />
            {user ? (
              <Button asChild className="rounded-full" data-testid="header-dashboard-button"><Link to="/dashboard">Dashboard</Link></Button>
            ) : (
              <>
                <Button asChild variant="ghost" size="sm" className="rounded-full sm:h-10 sm:px-4" data-testid="header-login-button"><Link to="/login">Masuk</Link></Button>
                <Button asChild size="sm" className="rounded-full sm:h-10 sm:px-4" data-testid="header-register-button"><Link to="/register">Daftar<span className="hidden sm:inline">&nbsp;gratis</span></Link></Button>
              </>
            )}
          </div>
        </div>
      </header>

      <section className="mx-auto max-w-6xl px-4 sm:px-6 pt-10 pb-16 sm:pt-16 sm:pb-24 lg:pt-24 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center overflow-hidden">
        <div className="lg:col-span-6 min-w-0 fade-up">
          <span className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-600">
            <span className="h-1.5 w-1.5 rounded-full bg-[#EE4D2D]" />Jualan affiliate jadi rapi
          </span>
          <h1 className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05] text-slate-900">
            Toko affiliate kamu, <span className="text-[#EE4D2D]">satu link</span> saja.
          </h1>
          <p className="mt-6 text-base sm:text-lg text-slate-600 max-w-lg leading-relaxed">
            Kumpulkan produk affiliate Shopee & TikTok dalam katalog yang cantik, bagikan link tokomu, dan lihat produk mana yang paling banyak diklik.
          </p>
          <div className="mt-8 sm:mt-10 flex flex-col sm:flex-row gap-3">
            <Button asChild size="lg" className="rounded-full h-12 px-6" data-testid="hero-register-button">
              <Link to="/register">Buat toko gratis <ArrowRight className="h-4 w-4 ml-1" /></Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="rounded-full h-12 px-6" data-testid="hero-login-button">
              <Link to="/login">Saya sudah punya akun</Link>
            </Button>
          </div>
          <div className="mt-8 sm:mt-10 flex items-center gap-2 text-xs sm:text-sm text-slate-500 min-w-0">
            <Link2 className="h-4 w-4 shrink-0" /><span className="font-mono truncate">{window.location.host}/nama-toko-kamu</span>
          </div>
        </div>
        <div className="lg:col-span-6 min-w-0 fade-up" style={{ animationDelay: "120ms" }}><StorePreview /></div>
      </section>

      <section className="bg-[#F8FAFC] border-y border-slate-200">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 py-16 sm:py-20">
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Fitur</p>
          <h2 className="mt-2 text-2xl sm:text-3xl font-extrabold tracking-tight max-w-xl">Semua yang dibutuhkan affiliator, tanpa ribet.</h2>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {FEATURES.map(({ icon: Icon, title, text, span }, i) => (
              <div key={title} className={`rounded-2xl border border-slate-200 bg-white p-6 hover:-translate-y-1 transition-transform duration-300 fade-up ${span || ""}`} style={{ animationDelay: `${i * 70}ms` }}>
                <Icon className="h-5 w-5 text-slate-900" />
                <h3 className="mt-4 font-bold">{title}</h3>
                <p className="mt-1 text-sm text-slate-600">{text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-4 sm:px-6 py-16 sm:py-20">
        <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Cara kerja</p>
        <h2 className="mt-2 text-2xl sm:text-3xl font-extrabold tracking-tight max-w-xl">Tiga langkah, toko langsung online.</h2>
        <ol className="mt-10 grid md:grid-cols-3 gap-8 md:gap-6">
          {STEPS.map((s, i) => (
            <li key={s.title} className="relative pl-14 md:pl-0 md:pt-16 fade-up" style={{ animationDelay: `${i * 80}ms` }}>
              <span className="absolute left-0 top-0 grid h-10 w-10 place-items-center rounded-full border border-slate-200 font-mono text-sm font-semibold">0{i + 1}</span>
              <h3 className="font-bold">{s.title}</h3>
              <p className="mt-1 text-sm text-slate-600">{s.text}</p>
            </li>
          ))}
        </ol>
      </section>

      <section className="px-4 sm:px-6 pb-16 sm:pb-20">
        <div className="mx-auto max-w-6xl rounded-3xl bg-slate-900 px-6 py-12 sm:px-12 sm:py-16 text-white flex flex-col md:flex-row md:items-center justify-between gap-8 relative overflow-hidden">
          <div className="absolute -right-20 -top-20 h-72 w-72 rounded-full bg-[#EE4D2D]/25 blur-3xl" />
          <div className="relative">
            <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight max-w-lg">Mulai jualan affiliate hari ini.</h2>
            <p className="mt-2 text-sm text-slate-400">Gratis, tanpa kartu kredit. Bisa di-install di HP seperti aplikasi.</p>
          </div>
          <Button asChild size="lg" className="relative rounded-full h-12 px-6 bg-white text-slate-900 hover:bg-slate-100" data-testid="cta-register-button"><Link to="/register">Daftar sekarang <ArrowRight className="h-4 w-4 ml-1" /></Link></Button>
        </div>
      </section>
      <footer className="border-t border-slate-200">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 py-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <Logo />
          <p className="text-xs text-slate-500">© {new Date().getFullYear()} {settings.app_title} — Jualan Affiliate</p>
        </div>
      </footer>
    </div>
  );
}
